import React, { useState, useEffect } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableRow, Paper,
    TableHead, Button, TextField, Dialog,
    DialogActions, DialogContent, DialogContentText, DialogTitle,
    Autocomplete, Grid, FormControlLabel, Checkbox, Box, Chip,
    IconButton, Typography
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Save as SaveIcon,
    Cancel as CancelIcon
} from '@mui/icons-material';
import { useUser } from '../contexts/UserContext';
import { useAccesos } from '../contexts/AccesosContext';
import Swal from 'sweetalert2';

function GestionPrestamosBancarios() {
    const server = process.env.REACT_APP_API_ROUTE;
    const { user } = useUser();
    const { tienePermiso } = useAccesos();

    const formatUser = (username) => {
        if (!username) username = 'ADMIN';
        // Quitar espacios en blanco al inicio y final
        username = username.trim();
        // Asegurar exactamente 20 caracteres
        if (username.length >= 20) return username.substring(0, 20);
        return username.padEnd(20, ' ');
    };

    // Estados principales
    const [prestamos, setPrestamos] = useState([]);
    const [bancos, setBancos] = useState([]);
    const [cuentasContables, setCuentasContables] = useState([]);

    // Estados del formulario
    const [numeroContrato, setNumeroContrato] = useState('');
    const [banco, setBanco] = useState(null);
    const [cuentaContable, setCuentaContable] = useState(null);
    const [montoOriginal, setMontoOriginal] = useState('');
    const [tasaInteres, setTasaInteres] = useState('');
    const [cuota, setCuota] = useState('');
    const [plazo, setPlazo] = useState('');
    const [fechaInicio, setFechaInicio] = useState('');
    const [fechaVencimiento, setFechaVencimiento] = useState('');
    const [activo, setActivo] = useState(true);

    // Estados de control
    const [editingPrestamo, setEditingPrestamo] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [prestamoToDelete, setPrestamoToDelete] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    // Cargar datos iniciales
    useEffect(() => {
        fetchPrestamos();
        fetchBancos();
        fetchCuentasContables();
    }, []);

    // Cargar datos del préstamo cuando se edita
    useEffect(() => {
        if (editingPrestamo) {
            setNumeroContrato(editingPrestamo.numeroContrato || '');
            setBanco(bancos.find(b => b.idBanco === editingPrestamo.idBanco) || null);
            setCuentaContable(cuentasContables.find(c => c.idCuenta === editingPrestamo.idCuentaContable) || null);
            setMontoOriginal(editingPrestamo.montoOriginal || '');
            setTasaInteres(editingPrestamo.tasaInteres || '');
            setCuota(editingPrestamo.cuota || '');
            setPlazo(editingPrestamo.plazo || '');
            setFechaInicio(editingPrestamo.fechaInicio ? editingPrestamo.fechaInicio.split('T')[0] : '');
            setFechaVencimiento(editingPrestamo.fechaVencimiento ? editingPrestamo.fechaVencimiento.split('T')[0] : '');
            setActivo(!editingPrestamo.isDelete);
        } else {
            limpiarFormulario();
        }
    }, [editingPrestamo, bancos, cuentasContables]);

    const fetchPrestamos = async () => {
        try {
            const response = await fetch(`${server}/ctb/prestamos-bancarios`);
            const data = await response.json();
            setPrestamos(data.$values || data || []);
        } catch (error) {
            console.error('Error al cargar préstamos:', error);
        }
    };

    const fetchBancos = async () => {
        try {
            const response = await fetch(`${server}/ChkBancoes`);
            const data = await response.json();
            setBancos(data.$values || data || []);
        } catch (error) {
            console.error('Error al cargar bancos:', error);
        }
    };

    const fetchCuentasContables = async () => {
        try {
            const response = await fetch(`${server}/CtbPlanDeCuentas`);
            const data = await response.json();
            const cuentas = (data.$values || []).filter(c => c.aceptaMovimientos === true);
            setCuentasContables(cuentas);
        } catch (error) {
            console.error('Error al cargar cuentas contables:', error);
        }
    };

    const limpiarFormulario = () => {
        setNumeroContrato('');
        setBanco(null);
        setCuentaContable(null);
        setMontoOriginal('');
        setTasaInteres('');
        setCuota('');
        setPlazo('');
        setFechaInicio('');
        setFechaVencimiento('');
        setActivo(true);
        setEditingPrestamo(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!numeroContrato.trim()) {
            Swal.fire({
                icon: 'warning',
                title: 'Campo requerido',
                text: 'El número de contrato es obligatorio',
            });
            return;
        }

        if (!banco) {
            Swal.fire({
                icon: 'warning',
                title: 'Campo requerido',
                text: 'El banco es obligatorio',
            });
            return;
        }

        const prestamo = {
            idPrestamo: editingPrestamo?.idPrestamo || 0,
            numeroContrato: numeroContrato.trim(),
            idBanco: banco.idBanco,
            idCuentaContable: cuentaContable?.idCuenta || null,
            montoOriginal: parseFloat(montoOriginal) || 0,
            tasaInteres: parseFloat(tasaInteres) || 0,
            cuota: parseFloat(cuota) || 0,
            plazo: parseInt(plazo) || 0,
            fechaInicio: fechaInicio || null,
            fechaVencimiento: fechaVencimiento || null,
            isDelete: !activo,
        };

        // Agregar campo de auditoría según sea creación o modificación
        if (editingPrestamo) {
            prestamo.modificadoPor = formatUser(user?.codigoUsuario || 'ADMIN');
            console.log('Enviando ModificadoPor:', JSON.stringify(prestamo.modificadoPor), 'Length:', prestamo.modificadoPor.length);
        } else {
            prestamo.ingresadoPor = formatUser(user?.codigoUsuario || 'ADMIN');
            console.log('Enviando IngresadoPor:', JSON.stringify(prestamo.ingresadoPor), 'Length:', prestamo.ingresadoPor.length);
        }

        const method = editingPrestamo ? 'PUT' : 'POST';
        const endpoint = editingPrestamo
            ? `${server}/ctb/prestamos-bancarios/${editingPrestamo.idPrestamo}`
            : `${server}/ctb/prestamos-bancarios`;

        try {
            const response = await fetch(endpoint, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(prestamo),
            });

            if (response.ok) {
                Swal.fire({
                    icon: 'success',
                    title: 'Éxito',
                    text: `Préstamo ${editingPrestamo ? 'actualizado' : 'creado'} correctamente`,
                    timer: 1500,
                    showConfirmButton: false,
                });
                limpiarFormulario();
                await fetchPrestamos();
            } else {
                let errorMessage = 'Error al guardar el préstamo';
                try {
                    const errorData = await response.json();
                    errorMessage = errorData.message || errorData.title || errorMessage;
                } catch {
                    const errorText = await response.text();
                    if (errorText.includes('auditoria_audittrail_userid_fkey')) {
                        errorMessage = 'Error de auditoría: Usuario no válido en el sistema';
                    } else {
                        errorMessage = errorText.substring(0, 200);
                    }
                }
                Swal.fire({
                    icon: 'error',
                    title: `Error ${response.status}`,
                    text: errorMessage,
                });
            }
        } catch (error) {
            console.error('Error en handleSubmit:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error de Conexión',
                text: 'No se pudo guardar el préstamo',
            });
        }
    };

    const handleDelete = async () => {
        if (!prestamoToDelete) return;

        try {
            const response = await fetch(
                `${server}/ctb/prestamos-bancarios/${prestamoToDelete.idPrestamo}/${encodeURIComponent(user?.codigoUsuario || 'ADMIN')}`,
                { method: 'DELETE' }
            );

            if (response.ok) {
                Swal.fire({
                    icon: 'success',
                    title: 'Éxito',
                    text: 'Préstamo eliminado correctamente',
                    timer: 1500,
                    showConfirmButton: false,
                });
                await fetchPrestamos();
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'No se pudo eliminar el préstamo',
                });
            }
        } catch (error) {
            console.error('Error en handleDelete:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error de Conexión',
                text: 'No se pudo eliminar el préstamo',
            });
        } finally {
            setOpenDialog(false);
            setPrestamoToDelete(null);
        }
    };

    const handleOpenDialog = (prestamo) => {
        setPrestamoToDelete(prestamo);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setPrestamoToDelete(null);
    };

    const startEditRow = (prestamo) => {
        setEditingPrestamo(prestamo);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Filtrar préstamos basados en búsqueda
    const filteredPrestamos = prestamos.filter(prestamo => {
        if (!searchQuery) return true;
        return (
            prestamo.numeroContrato?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            prestamo.banco?.nombreBanco?.toLowerCase().includes(searchQuery.toLowerCase())
        );
    });

    return (
        <div className="right_col" role="main">
            <div className="">
                <div className="page-title">
                    <div className="title_left">
                        <h3>Gestión de Préstamos Bancarios</h3>
                    </div>
                </div>
                <div className="clearfix"></div>

                <div className="row">
                    <div className="col-md-12 col-sm-12">
                        <div className="x_panel">
                            <div className="x_content">
                                <br />

                                {/* Formulario */}
                                <Paper sx={{ p: 2, mb: 3 }}>
                                    <Typography variant="h6" gutterBottom>
                                        {editingPrestamo ? 'Editar Préstamo' : 'Nuevo Préstamo'}
                                    </Typography>
                                    {editingPrestamo && (
                                        <Box sx={{
                                            backgroundColor: '#e3f2fd',
                                            border: '1px solid #2196f3',
                                            borderRadius: '4px',
                                            p: 1.5,
                                            mb: 2
                                        }}>
                                            <Typography variant="body2" color="primary">
                                                <strong>💡 Modo Edición:</strong> Estás editando el préstamo.
                                            </Typography>
                                        </Box>
                                    )}
                                    <form onSubmit={handleSubmit}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Número de Contrato"
                                                    value={numeroContrato}
                                                    onChange={(e) => setNumeroContrato(e.target.value)}
                                                    required
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={6}>
                                                <Autocomplete
                                                    options={bancos}
                                                    getOptionLabel={(option) => option.nombreBanco || ''}
                                                    value={banco}
                                                    onChange={(e, newValue) => setBanco(newValue)}
                                                    renderInput={(params) => (
                                                        <TextField {...params} label="Banco" required />
                                                    )}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={6}>
                                                <Autocomplete
                                                    options={cuentasContables}
                                                    getOptionLabel={(option) => `${option.codigo} - ${option.descripcion}` || ''}
                                                    value={cuentaContable}
                                                    onChange={(e, newValue) => setCuentaContable(newValue)}
                                                    renderInput={(params) => (
                                                        <TextField {...params} label="Cuenta Contable" />
                                                    )}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={3}>
                                                <TextField
                                                    fullWidth
                                                    label="Monto Original"
                                                    type="number"
                                                    inputProps={{ step: '0.01' }}
                                                    value={montoOriginal}
                                                    onChange={(e) => setMontoOriginal(e.target.value)}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={3}>
                                                <TextField
                                                    fullWidth
                                                    label="Tasa de Interés (%)"
                                                    type="number"
                                                    inputProps={{ step: '0.01' }}
                                                    value={tasaInteres}
                                                    onChange={(e) => setTasaInteres(e.target.value)}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={3}>
                                                <TextField
                                                    fullWidth
                                                    label="Cuota"
                                                    type="number"
                                                    inputProps={{ step: '0.01' }}
                                                    value={cuota}
                                                    onChange={(e) => setCuota(e.target.value)}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={3}>
                                                <TextField
                                                    fullWidth
                                                    label="Plazo (meses)"
                                                    type="number"
                                                    value={plazo}
                                                    onChange={(e) => setPlazo(e.target.value)}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Fecha de Inicio"
                                                    type="date"
                                                    value={fechaInicio}
                                                    onChange={(e) => setFechaInicio(e.target.value)}
                                                    InputLabelProps={{ shrink: true }}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Fecha de Vencimiento"
                                                    type="date"
                                                    value={fechaVencimiento}
                                                    onChange={(e) => setFechaVencimiento(e.target.value)}
                                                    InputLabelProps={{ shrink: true }}
                                                />
                                            </Grid>

                                            <Grid item xs={12}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={activo}
                                                            onChange={(e) => setActivo(e.target.checked)}
                                                        />
                                                    }
                                                    label="Activo"
                                                />
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Button
                                                    type="submit"
                                                    variant="contained"
                                                    color="primary"
                                                    startIcon={editingPrestamo ? <EditIcon /> : <AddIcon />}
                                                    sx={{ mr: 1 }}
                                                    disabled={!tienePermiso('Editar Préstamo Bancario')}
                                                >
                                                    {editingPrestamo ? 'Actualizar' : 'Guardar'}
                                                </Button>
                                                {editingPrestamo && (
                                                    <Button
                                                        variant="outlined"
                                                        onClick={limpiarFormulario}
                                                    >
                                                        Cancelar
                                                    </Button>
                                                )}
                                            </Grid>
                                        </Grid>
                                    </form>
                                </Paper>

                                <div className="ln_solid" />

                                {/* Tabla de préstamos */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1 }}>
                                    <Typography variant="h6">Préstamos Bancarios</Typography>
                                </Box>

                                <div className="row">
                                    <div className="col-md-12">
                                        <TextField
                                            fullWidth
                                            variant="outlined"
                                            label="Buscar préstamo"
                                            placeholder="Buscar por número de contrato o banco..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            size="small"
                                            style={{ marginBottom: '15px' }}
                                        />

                                        <div className="table-responsive">
                                            <TableContainer component={Paper}>
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>Nº Contrato</TableCell>
                                                            <TableCell>Banco</TableCell>
                                                            <TableCell align="right">Monto Original</TableCell>
                                                            <TableCell align="right">Tasa Interés</TableCell>
                                                            <TableCell align="right">Cuota</TableCell>
                                                            <TableCell align="right">Plazo</TableCell>
                                                            <TableCell>Fecha Inicio</TableCell>
                                                            <TableCell>Fecha Vencimiento</TableCell>
                                                            <TableCell align="center">Activo</TableCell>
                                                            <TableCell align="right">Acciones</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {filteredPrestamos.map((prestamo) => (
                                                            <TableRow key={prestamo.idPrestamo}>
                                                                <TableCell>{prestamo.numeroContrato}</TableCell>
                                                                <TableCell>{prestamo.banco?.nombreBanco}</TableCell>
                                                                <TableCell align="right">
                                                                    {prestamo.montoOriginal?.toFixed(2)}
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    {prestamo.tasaInteres?.toFixed(2)}%
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    {prestamo.cuota?.toFixed(2)}
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    {prestamo.plazo}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {prestamo.fechaInicio ? new Date(prestamo.fechaInicio).toLocaleDateString() : '-'}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {prestamo.fechaVencimiento ? new Date(prestamo.fechaVencimiento).toLocaleDateString() : '-'}
                                                                </TableCell>
                                                                <TableCell align="center">
                                                                    {!prestamo.isDelete ? '✓' : '-'}
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    <IconButton
                                                                        size="small"
                                                                        color="primary"
                                                                        onClick={() => startEditRow(prestamo)}
                                                                        disabled={!tienePermiso('Editar Préstamo Bancario')}
                                                                    >
                                                                        <EditIcon fontSize="small" />
                                                                    </IconButton>
                                                                    <IconButton
                                                                        size="small"
                                                                        color="error"
                                                                        onClick={() => handleOpenDialog(prestamo)}
                                                                        disabled={!tienePermiso('Eliminar Préstamo Bancario')}
                                                                    >
                                                                        <DeleteIcon fontSize="small" />
                                                                    </IconButton>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </div>

                                        <Dialog open={openDialog} onClose={handleCloseDialog}>
                                            <DialogTitle>Confirmar eliminación</DialogTitle>
                                            <DialogContent>
                                                <DialogContentText>
                                                    ¿Está seguro de eliminar el préstamo "{prestamoToDelete?.numeroReferencia}"?
                                                    Esta acción no se puede deshacer.
                                                </DialogContentText>
                                            </DialogContent>
                                            <DialogActions>
                                                <Button onClick={handleCloseDialog} color="primary">
                                                    Cancelar
                                                </Button>
                                                <Button onClick={handleDelete} color="error" autoFocus disabled={!tienePermiso('Eliminar Préstamo Bancario')}>
                                                    Eliminar
                                                </Button>
                                            </DialogActions>
                                        </Dialog>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default GestionPrestamosBancarios;
